import React from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Row,
  Col
} from 'reactstrap';
import messages from '../../constants/messages';
import {
  notifyInfo,
  notifyError
} from '../../utils/notifier';
import {
  activateCompany,
  deactivateCompany
} from '../../actions/companies/apiRequests';
import Page from '../shared/Page';
import Button from '../shared/Button';
import ActionModal from '../shared/ActivationModal';
import CompanyInfo from './CompanyInfo';
import CompanyLogo from './CompanyLogo';
import CompanyUsers from './CompanyUsers';
import SubscriptionPreferences from './SubscriptionPreferences';
import CompanyCounters from './CompanyCounters';
import Modal from '../shared/Modal';
import {
  MANAGE_COMPANIES,
  DASHBOARD,
  PAGE_PATHS
} from '../../constants/pathsRoots';
import { getBreadCrumbs } from '../../utils/breadCrumbs';

class CompanyDetailsPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      company: {
        ...props.company
      },
      saving: false,
      logo: null,
      logoDeleted: false,
      errors: {},
      activationModal: false,
      showValidationErrors: false,
      fileUploadError: false
    };
    this.onFieldChange = this.onFieldChange.bind(this);
    this.onFieldValidate = this.onFieldValidate.bind(this);
    this.onLoadLogo = this.onLoadLogo.bind(this);
    this.onClearLogo = this.onClearLogo.bind(this);
    this.onStatusChangeSuccess = this.onStatusChangeSuccess.bind(this);
    this.onStatusChangeError = this.onStatusChangeError.bind(this);
    this.toggleActivationModal = this.toggleActivationModal.bind(this);
    this.onSave = this.onSave.bind(this);
    this.showModalError = this.showModalError.bind(this);
    this.hideAttachmentUploadError = this.hideAttachmentUploadError.bind(this);
  }

  static get propTypes() {
    return {
      company: PropTypes.object,
      editInfo: PropTypes.bool,
      editUsers: PropTypes.bool,
      editLogo: PropTypes.bool,
      editIsn: PropTypes.bool,
      editStatus: PropTypes.bool,
      editSubscription: PropTypes.bool,
      editCustomerNumber: PropTypes.bool,
      showAdmins: PropTypes.bool,
      showSaveButton: PropTypes.bool,
      showStatusButton: PropTypes.bool,
      showSuccessNotification: PropTypes.bool,
      showCounters: PropTypes.bool,
      match: PropTypes.object,
      location: PropTypes.object,
      history: PropTypes.object,
      title: PropTypes.string,
      onSave: PropTypes.func,
      myCompany: PropTypes.bool
    };
  }

  static get defaultProps() {
    return {
      editInfo: true,
      editUsers: true,
      editLogo: true,
      editStatus: true,
      editSubscription: true,
      editCustomerNumber: true,
      showCounters: true,
      showAdmins: true,
      showSaveButton: true,
      showStatusButton: true,
      showSuccessNotification: true,
      myCompany: false
    };
  }

  get isValid() {
    const { errors } = this.state;
    return !Object.values(errors).filter(value => !!value).length;
  }

  get actions() {
    const {
      showSaveButton,
      showStatusButton
    } = this.props;
    const { enabled } = this.state.company;
    return [
      <If key='deactivate' condition={showStatusButton}>
        <Button
          kind='danger'
          className='mr-4'
          onClick={this.toggleActivationModal}
        >
          {enabled ? 'Deactivate' : 'Activate'}
        </Button>
      </If>,
      <Button
        kind='primary'
        key='cancel'
        className='mr-3'
        onClick={() => this.props.history.goBack()}>
          Cancel
        </Button>,
      <If condition={showSaveButton} key='save'>
        <Button
          key='save'
          kind='primary'
          disabled={this.state.saving}
          onClick={this.onSave}>
          {this.state.saving ?
            <i className='fa fa-spin fa-spinner' /> :
            'Save changes'
          }
        </Button>
      </If>
    ];
  }

  onFieldChange(name, value) {
    this.setState((prevState) => ({
      company: {
        ...prevState.company,
        [name]: value
      }
    }));
  }

  onFieldValidate(name, isValid) {
    this.setState((prevState) => ({
      errors: {
        ...prevState.errors,
        [name]: isValid
      }
    }));
  }

  onSave() {
    const {
      company,
      logo,
      logoDeleted
    } = this.state;
    const { onSave } = this.props;

    this.setState({ showValidationErrors: true });

    if (!this.isValid) {
      return;
    }

    this.setState({ saving: true });
    onSave(company, logo, logoDeleted)
      .then(() => this.showSuccessNotification())
      .catch(error => this.showErrorNotification(error))
      .finally(() => this.setState({ saving: false }))
  }

  onLoadLogo(file) {
    this.setState({
      logo: file
    });
  }

  onClearLogo() {
    this.setState({
      logo: null,
      logoDeleted: true
    });
  }

  onStatusChangeSuccess() {
    const { enabled } = this.state.company;
    this.onFieldChange('enabled', !enabled);
    this.toggleActivationModal();
    notifyInfo(messages.common.saveSuccess);
  }

  onStatusChangeError() {
    this.toggleActivationModal();
    notifyError(messages.common.error);
  }

  toggleActivationModal() {
    this.setState(({ activationModal }) => ({
      activationModal: !activationModal
    }));
  }

  showSuccessNotification() {
    if (this.props.showSuccessNotification) {
      notifyInfo(messages.common.saveSuccess);
    }
  }

  showErrorNotification({ response={} }) {
    let message = messages.common.error;
    if (response.errors.CustomerNumber) {
      message = messages.validation.customerNumberNotUnique;
      this.onFieldValidate('customerNumber', messages.validation.customerNumberNotUnique);
    }
    notifyError(message);
  }

  showModalError() {
    this.setState({
      fileUploadError: true
    });
  }

  hideAttachmentUploadError() {
    this.setState({
      fileUploadError: false
    });
  }

  render() {
    const {
      company,
      logo,
      logoDeleted,
      activationModal,
      errors,
      showValidationErrors,
      fileUploadError
    } = this.state;
    const {
      title,
      editInfo,
      editUsers,
      editIsn,
      editLogo,
      editStatus,
      editSubscription,
      editCustomerNumber,
      showAdmins,
      showCounters,
      location,
      myCompany
    } = this.props;
    const currentBreadCrumbs = myCompany ?
      [PAGE_PATHS[DASHBOARD]]
      : [PAGE_PATHS[DASHBOARD], PAGE_PATHS[MANAGE_COMPANIES]];

    return (
      <Page
        header={title}
        actions={this.actions}
        breadCrumbs={getBreadCrumbs(title, location.pathname, currentBreadCrumbs)}
      >
        <Container fluid className='px-0 mx-0'>
          <If condition={showCounters}>
            <Row className='text-right mb-2'>
              <Col>
                <CompanyCounters
                  lastMonthTotal={company.testsInPreviousMonth}
                  currentMonthTotal={company.testsInCurrentMonth}
                />
              </Col>
            </Row>
          </If>
          <Row>
            <Col lg={6}>
              <CompanyInfo
                company={company}
                edit={editInfo}
                editStatus={editStatus}
                editSubscription={editSubscription}
                editCustomerNumber={editCustomerNumber}
                createMode={this.createMode}
                errors={errors}
                showErrors={showValidationErrors}
                onFieldChange={this.onFieldChange}
                onFieldValidate={this.onFieldValidate}
              />
            </Col>
            <Col lg={6}>
              <CompanyLogo
                edit={editLogo}
                logoBase64={company.logoBase64}
                logo={logo}
                logoDeleted={logoDeleted}
                onLoad={this.onLoadLogo}
                onClear={this.onClearLogo}
                showModalError={this.showModalError}
              />
              <If condition={showAdmins}>
                <CompanyUsers
                  edit={editUsers}
                  users={company.admins}
                  companyId={company.id}
                />
              </If>
              <SubscriptionPreferences
                canEdit={editIsn}
                company={company}
                onFieldChange={this.onFieldChange}
              />
            </Col> 
          </Row>
        </Container>
        <Modal
          isOpen={fileUploadError}
          showCancelButton={false}
          onConfirm={this.hideAttachmentUploadError}
          header='File upload error'
        >
          The file could not be uploaded. Make sure the file has an image extension and its size less than 5Mb
        </Modal>
        <ActionModal
          message={`Are you sure you want to ${company.enabled ? 'deactivate' : 'activate'} this company?`}
          isOpen={activationModal}
          action={() => company.enabled ? deactivateCompany(company.id) : activateCompany(company.id)}
          onCancel={this.toggleActivationModal}
          onSuccess={this.onStatusChangeSuccess}
          onError={this.onStatusChangeError}
        />
      </Page>
    );
  }
}

export default CompanyDetailsPage;



// WEBPACK FOOTER //
// src/components/companyDetails/CompanyDetailsPage.jsx